import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CognitoUserAttribute } from "amazon-cognito-identity-js";
import { userPool, makeUser, makeAuthDetails, sessionToUser, normalizeEmail } from "./cognitoClient";
import { setPendingConfirmation, clearPendingConfirmation, setPendingReset, clearPendingReset } from "./pendingState";

const AuthContext = createContext(null);

// Re-validate the session periodically; the SDK refreshes tokens with the
// refresh token when the ID/access tokens are expired.
const SESSION_CHECK_MS = 5 * 60 * 1000;

const getSessionAsync = (cognitoUser) =>
  new Promise((resolve, reject) => {
    cognitoUser.getSession((err, session) => (err ? reject(err) : resolve(session)));
  });

/**
 * SPA-owned auth (Etapa A). Exposes the same surface the app consumed from
 * react-oidc-context — isAuthenticated, isLoading, user, error,
 * signinRedirect, removeUser — plus the explicit sign-in/sign-up operations
 * the new pages need.
 */
export const CustomAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const pendingChallengeRef = useRef(null); // { cognitoUser, userAttributes } for NEW_PASSWORD_REQUIRED

  const loadSession = useCallback(async () => {
    const current = userPool.getCurrentUser();
    if (!current) {
      setUser(null);
      return null;
    }
    try {
      const session = await getSessionAsync(current);
      const next = sessionToUser(session);
      setUser(next);
      return next;
    } catch (err) {
      // Refresh token expired/revoked → treat as signed out.
      current.signOut();
      setUser(null);
      return null;
    }
  }, []);

  // Restore session on mount, then keep it fresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadSession();
      if (!cancelled) setIsLoading(false);
    })();
    const timer = setInterval(loadSession, SESSION_CHECK_MS);
    const onVisible = () => { if (document.visibilityState === "visible") loadSession(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadSession]);

  /**
   * Password sign-in (SRP). Resolves to:
   *   { status: 'SIGNED_IN' }
   *   { status: 'NEW_PASSWORD_REQUIRED' }   → call completeNewPassword()
   *   { status: 'UNCONFIRMED', email }      → caller routes to /confirmar-conta
   * Rejects with the raw Cognito error otherwise.
   */
  const signIn = useCallback((email, password) =>
    new Promise((resolve, reject) => {
      const cognitoUser = makeUser(email);
      cognitoUser.authenticateUser(makeAuthDetails(email, password), {
        onSuccess: (session) => {
          setUser(sessionToUser(session));
          setError(null);
          resolve({ status: "SIGNED_IN" });
        },
        onFailure: (err) => {
          if (err?.code === "UserNotConfirmedException") {
            setPendingConfirmation(normalizeEmail(email));
            resolve({ status: "UNCONFIRMED", email: normalizeEmail(email) });
            return;
          }
          reject(err);
        },
        newPasswordRequired: (userAttributes) => {
          pendingChallengeRef.current = { cognitoUser, userAttributes };
          resolve({ status: "NEW_PASSWORD_REQUIRED" });
        },
      });
    }), []);

  /** Completes the NEW_PASSWORD_REQUIRED challenge started by signIn(). */
  const completeNewPassword = useCallback((newPassword) =>
    new Promise((resolve, reject) => {
      const pending = pendingChallengeRef.current;
      if (!pending) {
        reject(new Error("No pending new-password challenge"));
        return;
      }
      // Cognito rejects immutable/derived attributes if echoed back.
      const { email_verified, phone_number_verified, email, ...attrs } = pending.userAttributes || {};
      pending.cognitoUser.completeNewPasswordChallenge(newPassword, attrs, {
        onSuccess: (session) => {
          pendingChallengeRef.current = null;
          setUser(sessionToUser(session));
          resolve({ status: "SIGNED_IN" });
        },
        onFailure: reject,
      });
    }), []);

  /**
   * Creates the account. Cognito e-mails a 6-digit code; the journey is
   * persisted so /confirmar-conta can resume after a reload.
   * Resolves { status: 'CONFIRMATION_REQUIRED' | 'SIGNED_UP', email }.
   */
  const signUp = useCallback(({ email, password, firstName, lastName }) =>
    new Promise((resolve, reject) => {
      const username = normalizeEmail(email);
      const attributes = [
        new CognitoUserAttribute({ Name: "email", Value: username }),
        new CognitoUserAttribute({ Name: "given_name", Value: (firstName || "").trim() }),
        new CognitoUserAttribute({ Name: "family_name", Value: (lastName || "").trim() }),
      ];
      userPool.signUp(username, password, attributes, null, (err, result) => {
        if (err) { reject(err); return; }
        if (result?.userConfirmed) {
          resolve({ status: "SIGNED_UP", email: username });
          return;
        }
        setPendingConfirmation(username);
        resolve({ status: "CONFIRMATION_REQUIRED", email: username });
      });
    }), []);

  /** Confirms the account with the e-mailed code. Resolves on success. */
  const confirmSignUp = useCallback((email, code) =>
    new Promise((resolve, reject) => {
      makeUser(email).confirmRegistration((code || "").trim(), true, (err) => {
        if (err) { reject(err); return; }
        clearPendingConfirmation();
        resolve({ status: "CONFIRMED", email: normalizeEmail(email) });
      });
    }), []);

  /** Sends a fresh confirmation code. */
  const resendConfirmationCode = useCallback((email) =>
    new Promise((resolve, reject) => {
      makeUser(email).resendConfirmationCode((err) => {
        if (err) { reject(err); return; }
        setPendingConfirmation(normalizeEmail(email));
        resolve();
      });
    }), []);

  /** Starts password recovery: Cognito e-mails a code. Journey persisted for resume. */
  const forgotPassword = useCallback((email) =>
    new Promise((resolve, reject) => {
      makeUser(email).forgotPassword({
        onSuccess: () => {
          setPendingReset(normalizeEmail(email));
          resolve({ email: normalizeEmail(email) });
        },
        onFailure: reject,
        // Some SDK versions deliver the "code sent" event here instead of onSuccess.
        inputVerificationCode: () => {
          setPendingReset(normalizeEmail(email));
          resolve({ email: normalizeEmail(email) });
        },
      });
    }), []);

  /** Completes password recovery with the e-mailed code and the new password. */
  const confirmForgotPassword = useCallback((email, code, newPassword) =>
    new Promise((resolve, reject) => {
      makeUser(email).confirmPassword((code || "").trim(), newPassword, {
        onSuccess: () => {
          clearPendingReset();
          resolve({ email: normalizeEmail(email) });
        },
        onFailure: reject,
      });
    }), []);

  const signOut = useCallback(() => {
    const current = userPool.getCurrentUser();
    if (current) {
      // Best-effort global revoke; local sign-out happens regardless.
      current.globalSignOut({ onSuccess: () => {}, onFailure: () => {} });
      current.signOut();
    }
    pendingChallengeRef.current = null;
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    // --- react-oidc-context compatible surface ---
    isAuthenticated: !!user,
    isLoading,
    user,
    error,
    // In custom mode the route guard renders <Navigate to="/login" />; this is
    // kept so any stray caller does something sensible instead of crashing.
    signinRedirect: () => { window.location.assign("/login"); },
    removeUser: signOut,
    // --- explicit operations for the SPA auth pages ---
    signIn,
    completeNewPassword,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmForgotPassword,
    signOut,
    refreshSession: loadSession,
  }), [user, isLoading, error, signIn, completeNewPassword, signUp, confirmSignUp, resendConfirmationCode, forgotPassword, confirmForgotPassword, signOut, loadSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

CustomAuthProvider.propTypes = { children: PropTypes.node };

export const useCustomAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
